import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './SearchSelect.css';

const norm = s => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Combo buscable: mostrás el valor elegido; al tipear se filtra la lista en
// vivo. options: [{ key, label, data? }]. onSelect(option|null).
export default function SearchSelect({
  value,            // key de la opción seleccionada (o null)
  options,
  onSelect,
  placeholder = 'Buscar…',
  allowClear = true,
  clearLabel = 'Sin selección',
  extraOption,      // opción “legacy” fuera de la lista: { key, label }
  disabled,
  inputStyle,
  inputClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const selected = useMemo(
    () => options.find(o => String(o.key) === String(value)) || (extraOption && String(extraOption.key) === String(value) ? extraOption : null),
    [options, value, extraOption]
  );

  const filtered = useMemo(() => {
    const q = norm(query);
    const words = q.split(/\s+/).filter(Boolean);
    const base = extraOption ? [extraOption, ...options] : options;
    if (!words.length) return base;
    return base.filter(o => {
      const hay = norm(o.label);
      return words.every(w => hay.includes(w));
    });
  }, [options, extraOption, query]);

  useEffect(() => { setHi(0); }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const doClose = () => { setOpen(false); setQuery(''); };
    function handler(e) {
      if (inputRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      doClose();
    }
    function onScroll(e) {
      if (menuRef.current?.contains(e.target)) return;
      doClose();
    }
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', doClose);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', doClose);
    };
  }, [open]);

  function openMenu() {
    if (disabled) return;
    const r = inputRef.current?.getBoundingClientRect();
    if (r) setRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 240) });
    setQuery('');
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setQuery('');
  }

  function pick(opt) {
    close();
    onSelect(opt);
    inputRef.current?.blur();
  }

  function onKeyDown(e) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { e.preventDefault(); openMenu(); }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); close(); inputRef.current?.blur(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHi(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[hi]) pick(filtered[hi]);
    } else if (e.key === 'Tab') {
      close();
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        className={`form-input ss-input${inputClassName ? ` ${inputClassName}` : ''}`}
        style={inputStyle}
        disabled={disabled}
        placeholder={selected ? undefined : placeholder}
        value={open ? query : (selected?.label || '')}
        onChange={e => { if (!open) openMenu(); setQuery(e.target.value); }}
        onFocus={openMenu}
        onKeyDown={onKeyDown}
        autoComplete="off"
        spellCheck={false}
      />
      {open && rect && createPortal(
        <div
          ref={menuRef}
          className="ss-menu"
          style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, zIndex: 9999 }}
        >
          {allowClear && !query && (
            <button type="button" className="ss-option ss-option-clear" onMouseDown={e => { e.preventDefault(); pick(null); }}>
              {clearLabel}
            </button>
          )}
          {filtered.slice(0, 80).map((o, i) => (
            <button
              key={o.key}
              type="button"
              className={`ss-option${i === hi ? ' hi' : ''}${String(o.key) === String(value) ? ' selected' : ''}`}
              onMouseDown={e => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHi(i)}
            >
              {o.label}
              {String(o.key) === String(value) && <span className="ss-check">✓</span>}
            </button>
          ))}
          {filtered.length === 0 && <div className="ss-empty">Sin resultados</div>}
          {filtered.length > 80 && <div className="ss-empty">… seguí escribiendo para afinar ({filtered.length} resultados)</div>}
        </div>,
        document.body
      )}
    </>
  );
}
